import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Form, FormInstance, Input, Select } from 'antd';
import debounce from 'lodash/debounce';
import styled from 'styled-components';
import FormLabel from '../../../../shared/Form/FormLabel';
import { RoleNames } from '../../../../../store/mst/models';

interface Props {
  form: FormInstance,
  addMember: any,
  validateForm: (valid: boolean) => void,
  defaultRole?: RoleNames,
}
interface FormValues {
  email: string;
  role: RoleNames;
}

const StyledFormItem = styled(Form.Item)`
  .ant-form-item-explain {
    margin-top: 4px;
    margin-bottom: 0px;
  }

  .ant-form-item-explain-error {
    font-size: 12px;
    margin-bottom: 6px;
  }

  .ant-form-item-has-success {
    margin-bottom: 24px;
  }
`;

const AddMemberForm: React.FC<Props> = ({ addMember, form, validateForm, defaultRole = RoleNames.DISPATCHER }) => {
  const { t } = useTranslation('components');
  const [emailError, setEmailError] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    form.getFieldInstance('email').focus();
    form.setFieldsValue({
      role: defaultRole,
    });
  }, [defaultRole]);

  useEffect(() => {
    // only valid when fields have no errors
    validateForm(emailError === false);
  }, [emailError]);

  const handleFormInput = () => {
    if (form.isFieldTouched('email')) {
      setEmailError(form.isFieldTouched('email') && (form.getFieldError('email').length > 0));
    }
  };

  const onFinish = (values: FormValues) => {
    addMember(values);
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      data-cy="add-member-form"
      onValuesChange={debounce(handleFormInput, 500)}
      initialValues={{ role: defaultRole }}
    >
      <StyledFormItem
        name="email"
        data-cy="add-member-form-email"
        label={<FormLabel title={t('companySettings:team.addMemberForm.fields.email')} />}
        rules={[{ required: true, type: 'email', message: t('common:memberFields.email.invalidInput') }]}
      >
        <Input
          placeholder={t('common:memberFields.email.placeholder')}
          data-cy="add-member-form-email-input"
        />
      </StyledFormItem>
      <StyledFormItem
        name="role"
        label={<FormLabel title={t('companySettings:team.addMemberForm.fields.role')} />}
        rules={[{ required: true, message: t('common:memberFields.role.invalidInput') }]}
      >
        <Select
          placeholder={t('common:memberFields.role.placeholder')}
          data-cy="add-member-form-role-input"
          onSelect={handleFormInput}
          dropdownStyle={{ zIndex: 2001 }}
        >
          <Select.Option
            data-cy="add-member-form-role-input-select-dispatcher"
            value={RoleNames.DISPATCHER}>{t('companySettings:team.content.functionalRole.dispatcher')}
          </Select.Option>
          <Select.Option
            data-cy="add-member-form-role-input-select-readonly"
            value={RoleNames.READ_ONLY}>{t('companySettings:team.content.functionalRole.read_only')}
          </Select.Option>
          <Select.Option
            data-cy="add-member-form-role-input-select-driver"
            value={RoleNames.DRIVER}>{t('companySettings:team.content.functionalRole.driver')}
          </Select.Option>
        </Select>
      </StyledFormItem>
    </Form>
  );
};

export default observer(AddMemberForm);
